import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  ApiError,
  checkinPet,
  feedPet,
  getMyPet,
  recordAnalyticsEvent,
  updatePet,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import {
  PET_PROJECT_MIME,
  hasFeedableProject,
  navigateToPetAnalysis,
  readProjectDragData,
} from '../project-feed.js'
import { resolvePetRuntimeState } from '../pet-runtime/petStates.js'
import { resolvePetState } from './pet/petStateMachine.js'
import AIAssistantWidget from './AIAssistantWidget.jsx'
import AIPetPanel from './AIPetPanel.jsx'
import PetCompanion from './PetCompanion.jsx'

/* test-source-markers: 松手让我帮你分析这个项目；我有点饿啦，来喂我吧 */
const PET_POSITION_STORAGE_KEY = 'ai_pet_widget_position'
const PET_BUTTON_SIZE = { width: 112, height: 144 }
const DESKTOP_SAFE_MARGIN = 24
const MOBILE_SAFE_MARGIN = 16
const DRAG_CLICK_SUPPRESS_MS = 320
const BUTTON_SNAP_TRANSITION_MS = 180
const MOBILE_BREAKPOINT = 768
const PET_CLICK_PULSE_MS = 360
const PET_MOMENT_MS = 3200
const DEFAULT_PET = {
  petType: 'robot',
  petName: 'AI 小助手',
  level: 1,
  exp: 0,
  expToNextLevel: 100,
  foodCount: 0,
  mood: 'normal',
  displayMood: 'normal',
  foodHint: '没有食物了，去签到领取',
  hungryInHours: 0,
  lastFedAt: null,
  nextHungryAt: null,
  selectedSkin: 'robot',
  checkedInToday: false,
}

function isMobileViewport() {
  return window.innerWidth <= MOBILE_BREAKPOINT
}

function viewportBounds() {
  return {
    width: document.documentElement.clientWidth || window.innerWidth,
    height: document.documentElement.clientHeight || window.innerHeight,
  }
}

function currentSafeMargin() {
  return isMobileViewport() ? MOBILE_SAFE_MARGIN : DESKTOP_SAFE_MARGIN
}

function clampPosition(position, size = PET_BUTTON_SIZE) {
  const viewport = viewportBounds()
  const safeMargin = currentSafeMargin()
  return {
    x: Math.min(
      Math.max(safeMargin, position.x),
      Math.max(safeMargin, viewport.width - size.width - safeMargin),
    ),
    y: Math.min(
      Math.max(safeMargin, position.y),
      Math.max(safeMargin, viewport.height - size.height - safeMargin),
    ),
  }
}

function snapPosition(position, size = PET_BUTTON_SIZE) {
  const viewport = viewportBounds()
  const safeMargin = currentSafeMargin()
  const centerX = position.x + size.width / 2
  return clampPosition({
    x: centerX < viewport.width / 2
      ? safeMargin
      : viewport.width - size.width - safeMargin,
    y: position.y,
  }, size)
}

function readStoredPosition() {
  try {
    const stored = JSON.parse(localStorage.getItem(PET_POSITION_STORAGE_KEY) || 'null')
    if (!stored || !Number.isFinite(stored.x) || !Number.isFinite(stored.y)) return null
    return clampPosition(stored)
  } catch {
    return null
  }
}

function savePosition(position) {
  try {
    localStorage.setItem(PET_POSITION_STORAGE_KEY, JSON.stringify(position))
  } catch {
    // Local storage can be unavailable in private mode; dragging still works for this session.
  }
}

function removePointerListeners(drag) {
  if (!drag?.listeners) return
  window.removeEventListener('pointermove', drag.listeners.move)
  window.removeEventListener('pointerup', drag.listeners.finish)
  window.removeEventListener('pointercancel', drag.listeners.cancel)
}

function normalizePet(pet) {
  return {
    ...DEFAULT_PET,
    ...(pet || {}),
  }
}

function normalizedMood(pet) {
  const mood = pet?.displayMood || pet?.mood || 'normal'
  if (['happy', 'full', '开心', '饱饱的'].includes(mood)) return 'happy'
  if (['hungry', '饿了'].includes(mood)) return 'hungry'
  return 'normal'
}

function petStatusLine(pet, { dropActive = false, moment = '' } = {}, t) {
  const mood = normalizedMood(pet)
  const foodCount = Number(pet?.foodCount || 0)

  if (dropActive) return t('pet.drop')
  if (moment === 'catching') return t('pet.catching')
  if (moment === 'analyzing') return t('pet.thinking')
  if (moment === 'success') return t('pet.success')
  if (moment === 'sad') return t('pet.sad')
  if (moment === 'feeding') return t('pet.feeding')
  if (!pet?.checkedInToday) return t('pet.notChecked')
  if (foodCount <= 0) return t('pet.noFoodShort')
  if (mood === 'happy') return t('pet.happy')
  if (mood === 'hungry') return t('pet.hungry')
  return t('pet.idle')
}

function displayPetName(pet, t) {
  const name = String(pet?.petName || '').trim()
  if (!name || /灏|姪|鎵|�/.test(name)) return t('pet.name')
  return name
}

function AIPetWidget({ repoContext }) {
  const { user, loading: authLoading } = useAuth()
  const { t } = useLanguage()
  const location = useLocation()
  const navigate = useNavigate()
  const [panelOpen, setPanelOpen] = useState(false)
  const [assistantOpen, setAssistantOpen] = useState(false)
  const [mobileViewport, setMobileViewport] = useState(false)
  const [pet, setPet] = useState(DEFAULT_PET)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState('')
  const [error, setError] = useState('')
  const [skinPickerOpen, setSkinPickerOpen] = useState(false)
  const [buttonPosition, setButtonPosition] = useState(null)
  const [dragging, setDragging] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const [droppingProject, setDroppingProject] = useState(false)
  const [snapping, setSnapping] = useState(false)
  const [, setBubbleVisible] = useState(false)
  const [hovering, setHovering] = useState(false)
  const [clickPulse, setClickPulse] = useState(false)
  const [petMoment, setPetMoment] = useState('')
  const dragRef = useRef(null)
  const bubbleTimerRef = useRef(null)
  const clickPulseTimerRef = useRef(null)
  const petMomentTimerRef = useRef(null)
  const queuedPetMomentTimerRef = useRef(null)
  const suppressClickRef = useRef(false)
  const pointerToggleRef = useRef(false)
  const suppressClickTimerRef = useRef(null)
  const snapTimerRef = useRef(null)

  const displayPet = useMemo(() => normalizePet(pet), [pet])
  const isGuest = !authLoading && !user
  const avatarState = resolvePetState({
    actionLoading,
    dropActive,
    foodCount: displayPet.foodCount,
    hovering,
    moment: petMoment,
    mood: displayPet.displayMood || displayPet.mood,
  })
  const runtimeState = resolvePetRuntimeState({
    actionLoading,
    dropActive,
    moment: petMoment,
    petState: avatarState,
  })
  const bubbleText = petStatusLine(displayPet, {
    dropActive,
    moment: petMoment,
  }, t)
  const showBubble = !panelOpen
    && !assistantOpen
    && (dropActive
      || hovering
      || Boolean(petMoment))

  const trackPetEvent = useCallback((eventName, metadata = {}) => {
    recordAnalyticsEvent(eventName, {
      page: location.pathname,
      petType: displayPet.petType,
      ...metadata,
    }).catch(() => {})
  }, [displayPet.petType, location.pathname])

  const requireLogin = useCallback((message) => {
    setError(message)
    toast.info(message)
  }, [])

  const resetProjectDropState = useCallback(() => {
    setDropActive(false)
    setDroppingProject(false)
  }, [])

  const triggerPetMoment = useCallback((moment, duration = PET_MOMENT_MS) => {
    setPetMoment(moment)
    setBubbleVisible(true)
    window.clearTimeout(petMomentTimerRef.current)
    petMomentTimerRef.current = window.setTimeout(() => {
      setPetMoment('')
      setBubbleVisible(false)
    }, duration)
  }, [])

  const goLogin = useCallback(() => {
    navigate('/login', { state: { from: location.pathname } })
  }, [location.pathname, navigate])

  const loadPet = useCallback(async (signal) => {
    if (authLoading) return
    if (!user) {
      setPet(DEFAULT_PET)
      setLoading(false)
      setError('')
      return
    }

    setLoading(true)
    setError('')
    try {
      const result = await getMyPet(signal)
      setPet(normalizePet(result.pet))
    } catch (requestError) {
      if (requestError.name === 'AbortError') return
      if (requestError instanceof ApiError && requestError.status === 401) {
        setPet(DEFAULT_PET)
        setError('登录后可以保存宠物状态')
      } else {
        setError(requestError.message || '宠物状态加载失败')
      }
    } finally {
      setLoading(false)
    }
  }, [authLoading, user])

  useEffect(() => {
    setButtonPosition(readStoredPosition())
  }, [])

  useEffect(() => () => {
    removePointerListeners(dragRef.current)
    window.clearTimeout(bubbleTimerRef.current)
    window.clearTimeout(clickPulseTimerRef.current)
    window.clearTimeout(petMomentTimerRef.current)
    window.clearTimeout(queuedPetMomentTimerRef.current)
    window.clearTimeout(suppressClickTimerRef.current)
    window.clearTimeout(snapTimerRef.current)
  }, [])

  useEffect(() => {
    window.clearTimeout(bubbleTimerRef.current)
    if (!bubbleText || panelOpen || assistantOpen) {
      setBubbleVisible(false)
      return undefined
    }
    if (dropActive || petMoment || hovering) {
      setBubbleVisible(true)
      return undefined
    }
    setBubbleVisible(true)
    bubbleTimerRef.current = window.setTimeout(() => {
      setBubbleVisible(false)
    }, 3000)
    return () => window.clearTimeout(bubbleTimerRef.current)
  }, [assistantOpen, bubbleText, dropActive, hovering, panelOpen, petMoment])

  useEffect(() => {
    const resetDropState = () => resetProjectDropState()
    window.addEventListener('dragend', resetDropState)
    window.addEventListener('drop', resetDropState)
    return () => {
      window.removeEventListener('dragend', resetDropState)
      window.removeEventListener('drop', resetDropState)
    }
  }, [resetProjectDropState])

  useEffect(() => {
    const handleAnalysisSuccess = () => triggerPetMoment('success', 4200)
    const handleAnalysisFailed = () => triggerPetMoment('sad', 3200)
    const handleOpenPet = () => {
      setPanelOpen(true)
      setSkinPickerOpen(false)
    }
    window.addEventListener('ai-pet:analysis-success', handleAnalysisSuccess)
    window.addEventListener('ai-pet:analysis-failed', handleAnalysisFailed)
    window.addEventListener('ai-pet:open', handleOpenPet)
    return () => {
      window.removeEventListener('ai-pet:analysis-success', handleAnalysisSuccess)
      window.removeEventListener('ai-pet:analysis-failed', handleAnalysisFailed)
      window.removeEventListener('ai-pet:open', handleOpenPet)
    }
  }, [triggerPetMoment])

  useEffect(() => {
    const handleResize = () => {
      const nextMobile = isMobileViewport()
      setMobileViewport(nextMobile)
      setButtonPosition((current) => (current && !nextMobile ? clampPosition(current) : current))
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!panelOpen) return undefined
    trackPetEvent('pet_open')
    const controller = new AbortController()
    loadPet(controller.signal)
    return () => controller.abort()
  }, [loadPet, panelOpen, trackPetEvent])

  useEffect(() => {
    setPanelOpen(false)
    setSkinPickerOpen(false)
  }, [location.pathname])

  const moveButton = (event) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    event.preventDefault()
    const deltaX = event.clientX - drag.startX
    const deltaY = event.clientY - drag.startY
    if (Math.hypot(deltaX, deltaY) > 5) drag.moved = true
    const nextPosition = clampPosition({
      x: drag.originX + deltaX,
      y: drag.originY + deltaY,
    }, drag.size)
    drag.position = nextPosition
    setButtonPosition(nextPosition)
  }

  const finishButtonDrag = (event) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    dragRef.current = null
    removePointerListeners(drag)
    if (drag.element.hasPointerCapture(event.pointerId)) {
      drag.element.releasePointerCapture(event.pointerId)
    }

    const nextPosition = snapPosition(drag.position, drag.size)
    savePosition({
      x: Math.round(nextPosition.x),
      y: Math.round(nextPosition.y),
    })
    setButtonPosition({
      x: Math.round(nextPosition.x),
      y: Math.round(nextPosition.y),
    })

    if (drag.moved) {
      suppressClickRef.current = true
      window.clearTimeout(suppressClickTimerRef.current)
      suppressClickTimerRef.current = window.setTimeout(() => {
        suppressClickRef.current = false
      }, DRAG_CLICK_SUPPRESS_MS)
      setSnapping(true)
      window.clearTimeout(snapTimerRef.current)
      snapTimerRef.current = window.setTimeout(() => {
        setSnapping(false)
      }, BUTTON_SNAP_TRANSITION_MS)
    }
    setDragging(false)
  }

  const cancelButtonDrag = () => {
    const drag = dragRef.current
    if (!drag) return
    dragRef.current = null
    removePointerListeners(drag)
    if (drag.element.hasPointerCapture(drag.pointerId)) {
      drag.element.releasePointerCapture(drag.pointerId)
    }
    setDragging(false)
  }

  const startButtonDrag = (event) => {
    if (event.button !== 0) return
    if (mobileViewport || isMobileViewport()) return
    const rect = event.currentTarget.getBoundingClientRect()
    const drag = {
      element: event.currentTarget,
      listeners: {
        cancel: () => cancelButtonDrag(),
        finish: (pointerEvent) => finishButtonDrag(pointerEvent),
        move: (pointerEvent) => moveButton(pointerEvent),
      },
      moved: false,
      originX: rect.left,
      originY: rect.top,
      pointerId: event.pointerId,
      position: { x: rect.left, y: rect.top },
      size: { width: rect.width, height: rect.height },
      startX: event.clientX,
      startY: event.clientY,
    }
    dragRef.current = drag
    window.addEventListener('pointermove', drag.listeners.move)
    window.addEventListener('pointerup', drag.listeners.finish)
    window.addEventListener('pointercancel', drag.listeners.cancel)
    event.currentTarget.setPointerCapture(event.pointerId)
    setDragging(true)
  }

  const togglePetPanel = () => {
    setClickPulse(true)
    window.clearTimeout(clickPulseTimerRef.current)
    clickPulseTimerRef.current = window.setTimeout(() => {
      setClickPulse(false)
    }, PET_CLICK_PULSE_MS)
    setPanelOpen((open) => !open)
  }

  const openPetPanel = () => {
    if (pointerToggleRef.current) {
      pointerToggleRef.current = false
      return
    }
    if (suppressClickRef.current || dragRef.current?.moved) return
    togglePetPanel()
  }

  const handleFabPointerUp = () => {
    const drag = dragRef.current
    if (!drag || drag.moved || mobileViewport || isMobileViewport()) return
    pointerToggleRef.current = true
    window.setTimeout(() => {
      if (!suppressClickRef.current) togglePetPanel()
    }, 0)
  }

  const handleCheckin = async () => {
    if (!user) {
      requireLogin('登录后每天可以签到领取食物')
      return
    }
    setActionLoading('checkin')
    setError('')
    try {
      const result = await checkinPet()
      setPet(normalizePet(result.pet))
      trackPetEvent('pet_checkin', {
        checkedIn: result.checkedIn,
        rewardFood: result.rewardFood,
      })
      toast.success(result.message || '签到成功，食物 +1')
    } catch (requestError) {
      setError(requestError.message || '签到失败')
      toast.error(requestError.message || '签到失败')
    } finally {
      setActionLoading('')
    }
  }

  const handleFeed = async () => {
    if (!user) {
      requireLogin('登录后可以喂食宠物')
      return
    }
    setActionLoading('feed')
    setError('')
    try {
      const result = await feedPet()
      const latest = await getMyPet().catch(() => result)
      setPet(normalizePet(latest.pet || result.pet))
      trackPetEvent('pet_feed', {
        expGained: result.expGained,
        leveledUp: result.leveledUp,
      })
      triggerPetMoment('feeding')
      toast.success(result.message || '喂食成功，宠物 24 小时内不会饿哦')
    } catch (requestError) {
      setError(requestError.message || '喂食失败')
      toast.error(requestError.message || '喂食失败')
    } finally {
      setActionLoading('')
    }
  }

  const handleChangeType = async (petType) => {
    if (!user) {
      requireLogin('登录后可以保存宠物形象')
      return
    }
    setActionLoading('skin')
    setError('')
    try {
      const result = await updatePet({ petType, selectedSkin: petType })
      setPet(normalizePet(result.pet))
      trackPetEvent('pet_change_type', { nextPetType: petType })
      toast.success('宠物形象已更新')
    } catch (requestError) {
      setError(requestError.message || '更换形象失败')
      toast.error(requestError.message || '更换形象失败')
    } finally {
      setActionLoading('')
    }
  }

  const handleAskAi = () => {
    trackPetEvent('pet_go_ai')
    setPanelOpen(false)
    setAssistantOpen(true)
  }

  const handleStartGrowthGuide = () => {
    trackPetEvent('pet_start_growth_guide')
    setPanelOpen(false)
    navigate('/growth/explore/form?from=pet')
  }

  const handleGoGithubAnalysis = () => {
    trackPetEvent('pet_go_github_analysis')
    setPanelOpen(false)
    navigate('/github-analysis')
  }

  const canAcceptProjectDrop = (event) => (
    Array.from(event.dataTransfer?.types || []).includes(PET_PROJECT_MIME)
  )

  const handleProjectDragEnter = (event) => {
    if (!canAcceptProjectDrop(event)) return
    event.preventDefault()
    event.stopPropagation()
    setDropActive(true)
  }

  const handleProjectDragOver = (event) => {
    if (!canAcceptProjectDrop(event)) return
    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = 'copy'
    setDropActive(true)
  }

  const handleProjectDragLeave = (event) => {
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return
    resetProjectDropState()
  }

  const handleProjectDrop = (event) => {
    if (!canAcceptProjectDrop(event)) return
    event.preventDefault()
    event.stopPropagation()
    setDroppingProject(true)
    try {
      const payload = readProjectDragData(event.dataTransfer)
      if (!payload?.repoUrl || !hasFeedableProject(payload)) {
        toast.info('这个项目暂时没有可分析的 GitHub 地址')
        return
      }
      triggerPetMoment('catching', 1800)
      window.clearTimeout(queuedPetMomentTimerRef.current)
      queuedPetMomentTimerRef.current = window.setTimeout(() => {
        triggerPetMoment('analyzing', 4200)
      }, 900)
      trackPetEvent('project_drop_to_pet', payload)
      trackPetEvent('pet_analyze_project', {
        ...payload,
        trigger: 'drop',
      })
      toast.success('收到项目，正在准备分析')
      setPanelOpen(false)
      navigateToPetAnalysis(navigate, payload)
    } catch {
      toast.error('投喂失败，请稍后再试')
    } finally {
      resetProjectDropState()
    }
  }

  const widgetStyle = buttonPosition && !mobileViewport
    ? {
        bottom: 'auto',
        left: buttonPosition.x,
        right: 'auto',
        top: buttonPosition.y,
      }
    : undefined

  return (
    <>
      <aside
        className={[
          'ai-pet-widget',
          panelOpen ? 'is-open' : '',
          dragging ? 'is-dragging' : '',
          dropActive ? 'is-drop-active' : '',
          droppingProject ? 'is-dropping-project' : '',
          assistantOpen ? 'is-assistant-open' : '',
          snapping ? 'is-snapping' : '',
          clickPulse ? 'is-clicked' : '',
          petMoment ? `is-pet-${petMoment}` : '',
        ].filter(Boolean).join(' ')}
        onDragEnter={handleProjectDragEnter}
        onDragLeave={handleProjectDragLeave}
        onDragOver={handleProjectDragOver}
        onDrop={handleProjectDrop}
        onPointerEnter={() => setHovering(true)}
        onPointerLeave={() => setHovering(false)}
        style={widgetStyle}
      >
        <PetCompanion
          bubbleTone={dropActive ? 'drop' : 'default'}
          label={t('pet.open')}
          level={displayPet.level}
          message={showBubble ? bubbleText : ''}
          name={displayPetName(displayPet, t)}
          onClick={openPetPanel}
          onPointerDown={startButtonDrag}
          onPointerUp={handleFabPointerUp}
          open={panelOpen}
          state={runtimeState}
          title={t('pet.title')}
        />
      </aside>

      <div className="ai-pet-panel-layer">
        <AIPetPanel
          actionLoading={actionLoading}
          error={error}
          isGuest={isGuest}
          loading={loading}
          onAskAi={handleAskAi}
          onChangeType={handleChangeType}
          onCheckin={handleCheckin}
          onClose={() => setPanelOpen(false)}
          onFeed={handleFeed}
          onGoGithubAnalysis={handleGoGithubAnalysis}
          onLogin={goLogin}
          onStartGrowthGuide={handleStartGrowthGuide}
          onToggleSkinPicker={() => setSkinPickerOpen((open) => !open)}
          open={panelOpen}
          pet={displayPet}
          petState={avatarState}
          skinPickerOpen={skinPickerOpen}
        />
      </div>

      <AIAssistantWidget
        hideFloatingButton
        onOpenChange={setAssistantOpen}
        open={assistantOpen}
        repoContext={repoContext}
      />
    </>
  )
}

export default AIPetWidget
